import { createSelector } from 'reselect';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import I18n from 'i18n-js';
import { isFromMexApp } from 'helper/mex-app-helper';
import {
  isUsingPaxPersonalPayments,
  matchSelectedCompanyAndGroup,
} from 'helper/user';
import {
  DEFAULT_PARCEL_CATEGORY,
  PARCEL_CATEGORY,
} from '../constants/country-constants';
import { PAYSDK_PROFILE_TYPES } from '../constants/paysdk-constants';

const appSelector = state => state.app;

export const breadcumbPiecesSelector = createSelector(
  appSelector,
  app => app.breadcumbPieces,
);

export const totalCountHistorySelector = createSelector(
  appSelector,
  app => (isNaN(app.totalCount) ? 0 : app.totalCount),
);

export const invalidTokenSelector = createSelector(
  appSelector,
  app => app.invalidToken,
);

export const userInfoSelector = createSelector(
  appSelector,
  app => app.userInfo,
);

export const isPaxFromUserInfo = userInfo =>
  _get(userInfo, 'user.svc') === 'PASSENGER';

export const isPaxSelector = createSelector(
  userInfoSelector,
  isPaxFromUserInfo,
);

export const userSafeIDSelector = createSelector(userInfoSelector, userInfo =>
  _get(userInfo, 'user.safeID'),
);

export const userCompanySelector = createSelector(userInfoSelector, userInfo =>
  _get(userInfo, 'currentCompany'),
);

export const userCurrentGroupSelector = createSelector(
  userInfoSelector,
  userInfo => _get(userInfo, 'currentGroup'),
);

export const selectedCitySelector = createSelector(userInfoSelector, userInfo =>
  _get(userInfo, 'selectedCity'),
);

export const paxCountryCodeSelector = createSelector(
  selectedCitySelector,
  selectedCity => _get(selectedCity, 'countryCode'),
);

export const gfbCountryCodeSelector = createSelector(
  userCompanySelector,
  userCompany => _get(userCompany, 'countryCode'),
);

export const getUserCountryCodeFromState = state => {
  if (isPaxSelector(state)) {
    return paxCountryCodeSelector(state);
  } else {
    return gfbCountryCodeSelector(state);
  }
};

export const enterpriseSelector = createSelector(
  userInfoSelector,
  userCompanySelector,
  userCurrentGroupSelector,
  getUserCountryCodeFromState,
  (userInfo, company, group, countryCode) =>
    _get(company, 'id') &&
    _get(group, 'id') && {
      username: _get(userInfo, 'user.name'),
      companyName: _get(company, 'name'),
      companyID: _get(company, 'id'),
      groupName: _get(group, 'name'),
      groupID: _get(group, 'id'),
      countryCode,
    },
);

/**
 * Enterprise data for PaySDK (groupID, profileType).
 * - Pax personal: { profileType: PERSONAL }
 * - Pax/GFB enterprise: { groupID, profileType: CORPORATE } from current group
 *
 * For PAX users who switched to a business profile, also checks the stored
 * selection against userInfo.companies as fallback when currentCompany/currentGroup
 * haven't been set in Redux yet (race condition with PAX_FETCH_BUSINESS_PROFILES).
 *
 * Note: plain function (not createSelector) because the fallback branch reads
 * localStorage via isUsingPaxPersonalPayments() and matchSelectedCompanyAndGroup(),
 * which are outside Redux state and would break memoization.
 */
export const enterpriseForPaySdkSelector = state => {
  const company = userCompanySelector(state);
  const group = userCurrentGroupSelector(state);
  const userInfo = userInfoSelector(state);

  const hasCompanyAndGroup = _get(company, 'id') && _get(group, 'id');
  if (hasCompanyAndGroup) {
    return {
      groupID: _get(group, 'id'),
      profileType: _get(group, 'profileType') || PAYSDK_PROFILE_TYPES.CORPORATE,
    };
  }

  // Fallback for PAX users with business profile selected
  if (isPaxFromUserInfo(userInfo) && !isUsingPaxPersonalPayments()) {
    const companies = _get(userInfo, 'companies', []);
    const selected = matchSelectedCompanyAndGroup(companies);
    if (selected.company && selected.group) {
      return {
        groupID: _get(selected.group, 'id'),
        profileType:
          _get(selected.group, 'profileType') || PAYSDK_PROFILE_TYPES.CORPORATE,
      };
    }
  }

  return { profileType: PAYSDK_PROFILE_TYPES.PERSONAL };
};

export const userCompanyIDSelector = createSelector(
  userCompanySelector,
  company => _get(company, 'id'),
);

export const citySelector = createSelector(appSelector, app => app.city);

export const unhappyNotificationSelector = createSelector(
  appSelector,
  app => app.unhappyNotifications,
);

export const activeHistoryQtySelector = createSelector(
  appSelector,
  app => app.activeHistoryQty,
);
// if sum errors of FETCH_HISTORY action more than five, don't poll data.
export const isExceedRetryingFetchingSelector = createSelector(
  appSelector,
  app => app.isExceedRetryingFetching,
);

export const isFetchingServiceTypeErrorsSelector = createSelector(
  appSelector,
  app => app.isFetchingServiceTypeErrors,
);

export const isRequiredOrderIDSelector = createSelector(
  appSelector,
  app => app.isRequiredOrderID,
);
export const isBulkBookingWithExcelUploadSelector = createSelector(
  appSelector,
  app => app.bulkBookingWithExcelUpload,
);
export const isCashPaymentDisabledSelector = createSelector(
  appSelector,
  app => app.cashPaymentDisabled,
);
export const enableCreateDeliveriesV2Selector = createSelector(
  appSelector,
  app => app.enableCreateDeliveriesV2,
);
export const listCitiesSelector = createSelector(
  appSelector,
  app => app.cities,
);

export const isCashlessPaymentDisabledSelector = createSelector(
  appSelector,
  app => app.isCashlessPaymentDisabled,
);

export const failedPaymentsSelector = createSelector(
  appSelector,
  app => app.failedPayments,
);

export const failedPaymentsRetryStatusSelector = createSelector(
  appSelector,
  app => app.failedPaymentsRetryStatus,
);

export const enableShopifyOrdersSelector = createSelector(
  appSelector,
  app => app.enableShopifyOrders && !isFromMexApp(),
);

// feature flag selectors
export const enableAnytimeBookingUIBookingSelector = createSelector(
  appSelector,
  app => app.enableAnytimeBookingUIBooking,
);

// feature flag selectors
export const enableWebCancellationSelector = createSelector(
  appSelector,
  app => app.enableWebCancellation,
);

export const compulsoryCODSelector = createSelector(
  appSelector,
  app => app.compulsoryCOD,
);

export const enableBookingPageRevampSelector = createSelector(
  appSelector,
  app => app.enableBookingPageRevamp,
);

export const ongoingBookingRefreshIntervalInMsSelector = createSelector(
  appSelector,
  app => app.ongoingBookingRefreshIntervalInMs,
);

export const enableCancellationRulesSelector = createSelector(
  appSelector,
  app => app.enableCancellationRules,
);

export const showInTransitQRCodeSelector = createSelector(
  appSelector,
  app => app.showInTransitQRCode,
);

export const enableDeliveryGuaranteeSelector = createSelector(
  appSelector,
  app => app.enableDeliveryGuarantee,
);

export const itemCategoriesFromBackendSelector = createSelector(
  appSelector,
  app => app.itemCategories,
);

export const itemCategoriesSelector = createSelector(
  itemCategoriesFromBackendSelector,
  getUserCountryCodeFromState,
  (itemCategories, countryCode) => {
    if (!_isEmpty(itemCategories)) {
      return itemCategories;
    }
    let parcelCategories = PARCEL_CATEGORY[countryCode];
    if (!countryCode || !parcelCategories) {
      parcelCategories = DEFAULT_PARCEL_CATEGORY;
    }
    return parcelCategories.map(id => ({
      id,
      name: I18n.t(`v2.homepage.manualOrder.category.${id}`),
    }));
  },
);

export const showEnterpriseTripInputSelector = createSelector(
  isPaxSelector,
  isPax => {
    // Show enterprise trip input for:
    // 1. Non-pax users (GFB users)
    // 2. Pax users who have switched to enterprise profile
    if (!isPax) return true;

    // For pax users, check if they're using enterprise profile
    return !isUsingPaxPersonalPayments();
  },
);

export const featureFlagsSelector = createSelector(appSelector, app => ({
  enableBigParcelHandler: true,
  enableMSD: _get(app, 'enableMSD', false),
}));

export const isItemTypeMandatorySelector = createSelector(
  appSelector,
  getUserCountryCodeFromState,
  (app, countryCode) => {
    return _get(app, 'enableMandatoryItemType', []).includes(countryCode);
  },
);

export const enableItemTypeFreeTextSelector = createSelector(
  appSelector,
  getUserCountryCodeFromState,
  (app, countryCode) => {
    return _get(app, 'enableFreeTextItemType', []).includes(countryCode);
  },
);

export const quoteRequestConfigSelector = createSelector(appSelector, app =>
  _get(app, 'quoteRequestConfig'),
);

export const expressPaxHomeV2AnnouncementBannerDataSelector = createSelector(
  appSelector,
  app => _get(app, 'bannerConfigV2'),
);

export const geMaintenanceBannerDataSelector = createSelector(
  appSelector,
  app => _get(app, 'geWebMaintenanceBannerConfig'),
);

export const maintenanceBannerDismissedSelector = createSelector(
  appSelector,
  app => _get(app, 'maintenanceBannerDismissed', false),
);

export const enableGetUserProfilesEndpointSelector = createSelector(
  appSelector,
  app => _get(app, 'enableGetUserProfilesEndpoint', false),
);

export const enableGetPermissionsEndpointSelector = createSelector(
  appSelector,
  app => _get(app, 'enableGetPermissionsEndpoint', false),
);

export const enableMSDSelector = createSelector(appSelector, app =>
  _get(app, 'enableMSD', false),
);

export const enablePaySDKWebSelector = createSelector(appSelector, app =>
  _get(app, 'enableExpressWebPaySDK', false),
);
